/*******************************************************************************
* Copyright (c) Microsoft Corporation.
* All rights reserved. See LICENSE in the project root for license information.
*******************************************************************************/


import 'custom-event-polyfill';

import { initializeClientApp, clientRender } from '@msdyn365-commerce/runtime-internal/dist/lib/client';
const { getUniqueRequestedAssets } = require('../common');

// Fetches the registration object from a given file and dictionary name
// If the module is not found or dictionary does not exist an empty object is returned
const fetchRegistrationObject = (registration, dictionaryName) => {
    return registration ? registration[dictionaryName] : {};
};

// Registers all the local and installed modules need to render the page client-side
const registerModules = () => {
    const bindings = window.__bindings__;
    // installed modules registration
    if (bindings) {
        return {
            modules: {
                ...bindings.modules
            },
            dataActions: {
                // load data actions in the order of the action overrides (installed and local)
                ...bindings.dataActions
            },
            partnerDataActions: {
                ...bindings.packageDataActions['__local__']
            },
            views: {
                ...bindings.viewDictionary
            }
        };
    }

    return {
        modules: {},
        dataActions: {},
        partnerDataActions: {},
        views: {}
    };
};

const initializeConnectors = (connectorInformation = {}, connectorsRegistration, requestContext) => {
    let experimentationConnector;
    if (connectorsRegistration && connectorInformation) {
        const expConnectorName = connectorInformation.expConnector;
        if (expConnectorName && connectorsRegistration[expConnectorName]) {
            // Grab the stored config from the window object
            const config = window.___initialData___.experimentationConfig;
            // Grab the experimentation user ID
            const expUserId = requestContext && requestContext.experiments && requestContext.experiments.userId;
            // Initialize the provider and listener for client
            const experimentationProvider = connectorsRegistration[expConnectorName].provider;
            const experimentationListener = connectorsRegistration[expConnectorName].listener;

            if (!experimentationProvider || !experimentationListener) {
                return { experimentation: undefined };
            }

            experimentationProvider.initializeClientSide(config);
            experimentationListener.initializeClientSide(config, expUserId);
            experimentationConnector = { name: expConnectorName, provider: experimentationProvider, listener: experimentationListener };
        }
    }
    return { experimentation: experimentationConnector };
};

const initializePerformanceUtils = async bindings => {
    return import(/* webpackChunkName: "msdyn365-performance-chunk" */ '@msdyn365-commerce/commerce-performance-analyzer').then(lib => {
        bindings.perfAnalysisLibBinding = lib;
    });
};

const initializeClient = async () => {
    const requestedAssets = window.___initialData___ && getUniqueRequestedAssets(window.___initialData___);
    let bindings = { modules: {}, dataActions: {} };
    const requestContext = window.___initialData___ && window.___initialData___.requestContext;

    bindings = window.___initialData___ && registerModules(requestContext, requestedAssets);

    // component extensions registration
    try {
        bindings.components = fetchRegistrationObject(require('lib/component-registration'), 'componentDictionary');
    } catch (e) {
        if (e.code === 'MODULE_NOT_FOUND') {
            process.env.NODE_ENV === 'development' && console.log(`component registration files not found.`);
        }
    }

    // connectors registration
    try {
        bindings.connectors = fetchRegistrationObject(require('lib/connector-registration'), 'connectorDictionary');
    } catch (e) {
        if (e.code === 'MODULE_NOT_FOUND') {
            process.env.NODE_ENV === 'development' && console.log(`connector registration files not found.`);
        }
    }

    const connectorInformation = requestContext.connectors;
    const connectors = initializeConnectors(connectorInformation, bindings.connectors, requestContext);

    // eslint-disable-next-line camelcase
    const shouldRunPerformanceAnalysis = requestContext.features?.enable_performance_analysis && requestContext.params.isPerfAnalysis;
    if (shouldRunPerformanceAnalysis) {
        await initializePerformanceUtils(bindings);
    }

    await initializeClientApp(bindings, connectors);
    clientRender();
};

Promise.resolve().then(async () => {
    const initializeClientWithRetry = async retry => {
        if (retry > 3) {
            console.warn(`client initialization retry limit reached, Retry count: ${retry}`);
            // all retries failed but we should still render the page with the modules that are registered
            // safe render module will wrap the missing modules
            await initializeClient();
            return;
        }

        // Ideally, all the modules should be loaded by this time
        // However, loading does not guarantee execution of the script
        // in which case we should retry after a time window
        const bindings = window.__bindings__;
        if (!bindings) {
            setTimeout(() => initializeClientWithRetry(retry + 1), 500);
            return;
        }

        const lazyLoadedModules = window.___initialData___.requestCache.internalCache.__CEP__ || {};
        const modulesList = window.___initialData___._moduleList;
        const themesList = window.___initialData___._themeModulesList;
        const clientModulesList = new Set();
        modulesList.map(m => {
            if (m.typeName !== 'core-root' && !lazyLoadedModules[m.typeName.toUpperCase()]) {
                clientModulesList.add(m.typeName);
            }
        });
        themesList.map(m => m !== '' && clientModulesList.add(m));

        // make sure all the module chunks are loaded and executed before initializing the client
        let scriptsExecuted = true;
        Array.from(clientModulesList).map(m => {
            if (!bindings || !bindings.modules[m]) {
                scriptsExecuted = false;
            }
        });

        if (!bindings.packageDataActions?.['__local__']) {
            scriptsExecuted = false;
        }

        if (!scriptsExecuted) {
            setTimeout(() => initializeClientWithRetry(retry + 1), 500);
            return;
        }

        if (scriptsExecuted) {
            await initializeClient();
        } else {
            console.error(`client initialization failed, module scripts execution not complete. Retry count: ${retry}`);
            throw new Error(`client initialization failed, module scripts execution not complete. Retry count: ${retry}`);
        }
    };

    await initializeClientWithRetry(0);
});
